/**
 * Agent context for managing global agent drawer state.
 *
 * Supports three tiers of context for the omnipresent agent:
 *   Tier 1 — Metadata: entity IDs, resolved names, current view, user role
 *   Tier 2 — Viewport: serialized snapshot of what the user currently sees
 *   Tier 3 — Tools: the agent fetches deeper context on demand (handled by Node service)
 */
import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

// ── Agent surface geometry ──────────────────────────────────────────────────────

/** Full-page agent route (`/programs/:id/agent`) — the docked pane is hidden there. */
const AGENT_FULLPAGE_RE = /\/programs\/\d+\/agent$/;

/**
 * Whether the omnipresent agent renders on a given path. The agent lives on
 * program and standalone-market surfaces, but not on the dedicated full-page
 * agent route. Shared by the pane itself and by `AppShell`, which reserves the
 * right-hand column when the pane is open so page content reflows beside it
 * (a docked pane, not an overlay).
 */
export const isAgentSurfacePath = (path: string): boolean =>
  (path.startsWith('/programs') || path.startsWith('/market')) && !AGENT_FULLPAGE_RE.test(path);

/** Width (px) the agent pane reserves from the right edge: pane + outer + inner gaps. */
export const AGENT_PANE_RESERVE_PX = 440;

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PageContext {
  programId?: number;
  vendorId?: number;
  scorecardId?: number;
  documentId?: number;
  currentView?: string;
  /** Resolved entity names — avoids tool calls for basic orientation */
  programName?: string;
  programStatus?: string;
  vendorName?: string;
  /** Active sub-tab within a view */
  activeTab?: string;
  /** Workflow module page (`currentView === 'workflow_step'`): step key from the URL */
  stepKey?: string;
  /** Workflow module page: ProgramStep id + live substage + name, set by ModulePage */
  stepId?: number;
  substage?: string;
  stepName?: string;
  [key: string]: any;
}

export interface ViewportContext {
  view: string;
  capturedAt: string;
  estimatedTokens?: number;
  data: Record<string, any>;
}

export interface AgentContextPayload {
  metadata: PageContext;
  viewport?: ViewportContext;
}

/** Function that serializes the current view's data into a plain object */
export type ViewportSerializer = () => Record<string, any>;

interface AgentContextType {
  isOpen: boolean;
  openDrawer: () => void;
  /** Open the drawer and seed it with a user message to send on open */
  openDrawerWith: (message: string) => void;
  /** Read + clear any pending message queued by openDrawerWith */
  consumePendingMessage: () => string | null;
  closeDrawer: () => void;
  toggleDrawer: () => void;
  /** Tier 1 metadata — always available */
  pageContext: PageContext;
  updatePageContext: (context: Partial<PageContext>) => void;
  conversationId: string | null;
  setConversationId: (id: string | null) => void;
  startNewConversation: () => void;
  /** Tier 2 viewport registration */
  registerViewport: (viewName: string, serializer: ViewportSerializer) => void;
  unregisterViewport: (viewName: string) => void;
  /** Build the full context payload (tiers 1 + 2) for sending with a message */
  buildContextPayload: () => AgentContextPayload;
}

// ── Route → context ───────────────────────────────────────────────────────────

/** Workflow module page: `/programs/:programId/module/:stepKey`. */
const WORKFLOW_STEP_RE = /^\/programs\/(\d+)\/module\/([^/]+)\/?$/;

/**
 * Derive the route-owned part of the agent's Tier 1 metadata from a pathname.
 * Pure, so the mapping is testable without a router.
 */
export function deriveRouteContext(pathname: string): PageContext {
  const pathParts = pathname.split('/');
  const newContext: PageContext = {};

  // Check for program ID in URL (/programs/:programId/...)
  const programIndex = pathParts.indexOf('programs');
  if (programIndex !== -1 && pathParts[programIndex + 1]) {
    const programId = parseInt(pathParts[programIndex + 1], 10);
    if (!isNaN(programId)) {
      newContext.programId = programId;
    }
  }

  // Workflow module pages first: a step key is free text, so the substring
  // checks below would misfire on keys like `market_research` (`/market`) or
  // `documentation`.
  const stepMatch = pathname.match(WORKFLOW_STEP_RE);
  if (stepMatch) {
    newContext.currentView = 'workflow_step';
    newContext.stepKey = decodeURIComponent(stepMatch[2]);
    return newContext;
  }

  // Check for vendor ID in URL (/vendors/:vendorId or /programs/:programId/vendors/:vendorId)
  const vendorIndex = pathParts.indexOf('vendors');
  if (vendorIndex !== -1 && pathParts[vendorIndex + 1]) {
    const vendorId = parseInt(pathParts[vendorIndex + 1], 10);
    if (!isNaN(vendorId)) {
      newContext.vendorId = vendorId;
    }
  }

  // Determine current view from path segments
  if (pathname.includes('/requirements')) {
    newContext.currentView = 'requirements';
  } else if (pathname.includes('/setup/scorecards')) {
    newContext.currentView = 'scorecard_edit';
  } else if (pathname.includes('/setup/documents')) {
    newContext.currentView = 'documents';
  } else if (pathname.includes('/setup/process')) {
    newContext.currentView = 'timeline';
  } else if (pathname.includes('/setup/team')) {
    newContext.currentView = 'team';
  } else if (pathname.includes('/vendors/marketplace')) {
    newContext.currentView = 'vendor_marketplace';
  } else if (pathname.includes('/vendors/') && vendorIndex !== -1 && pathParts[vendorIndex + 1]) {
    newContext.currentView = 'vendor_detail';
  } else if (pathname.includes('/vendors')) {
    newContext.currentView = 'program_vendors';
  } else if (pathname.includes('/market')) {
    // Distinguish between program-scoped and standalone MarketView
    newContext.currentView = newContext.programId ? 'program_market' : 'market_view';
  } else if (pathname.includes('/documentation')) {
    newContext.currentView = 'documentation';
  } else if (AGENT_FULLPAGE_RE.test(pathname)) {
    newContext.currentView = 'program_agent';
  } else if (pathname.includes('/programs')) {
    newContext.currentView = 'program_overview';
  } else if (pathname.startsWith('/exec')) {
    // Exec pages — triggers exec tool set in the agent loop
    if (pathname.includes('/goals')) {
      newContext.currentView = 'exec_goals';
    } else if (pathname.includes('/portfolio-groups')) {
      newContext.currentView = 'exec_portfolios';
    } else if (pathname.includes('/explore')) {
      newContext.currentView = 'exec_explore';
    } else if (pathname.includes('/dashboard')) {
      newContext.currentView = 'exec_dashboard';
    } else {
      newContext.currentView = 'exec_home';
    }
  } else if (pathname.includes('/home')) {
    newContext.currentView = 'home';
  }

  return newContext;
}

const AgentContext = createContext<AgentContextType | undefined>(undefined);

// ── Provider ──────────────────────────────────────────────────────────────────

interface AgentProviderProps {
  children: ReactNode;
}

export const AgentProvider: React.FC<AgentProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pageContext, setPageContext] = useState<PageContext>({});
  const [conversationId, setConversationIdState] = useState<string | null>(null);
  const location = useLocation();

  // Viewport serializer registry — keyed by view name
  const viewportSerializers = useRef<Map<string, ViewportSerializer>>(new Map());

  // Queued message to seed the drawer's chat with on open (set by openDrawerWith)
  const pendingMessageRef = useRef<string | null>(null);

  const openDrawer = useCallback(() => {
    setIsOpen(true);
  }, []);

  const openDrawerWith = useCallback((message: string) => {
    pendingMessageRef.current = message?.trim() || null;
    setIsOpen(true);
  }, []);

  const consumePendingMessage = useCallback(() => {
    const msg = pendingMessageRef.current;
    pendingMessageRef.current = null;
    return msg;
  }, []);

  const closeDrawer = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleDrawer = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const updatePageContext = useCallback((context: Partial<PageContext>) => {
    setPageContext((prev) => ({ ...prev, ...context }));
  }, []);

  const setConversationId = useCallback((id: string | null) => {
    console.log('💾 AgentContext: Setting conversation ID:', id);
    setConversationIdState(id);
  }, []);

  const startNewConversation = useCallback(() => {
    console.log('🆕 AgentContext: Starting new conversation');
    setConversationIdState(null);
  }, []);

  // ── Viewport registration ───────────────────────────────────────────

  const registerViewport = useCallback((viewName: string, serializer: ViewportSerializer) => {
    viewportSerializers.current.set(viewName, serializer);
  }, []);

  const unregisterViewport = useCallback((viewName: string) => {
    viewportSerializers.current.delete(viewName);
  }, []);

  /**
   * Build the full context payload by combining metadata (tier 1)
   * with the active viewport serializer (tier 2).
   */
  const buildContextPayload = useCallback((): AgentContextPayload => {
    const payload: AgentContextPayload = {
      metadata: { ...pageContext },
    };

    // Call the most recently registered viewport serializer
    // (there should only be one active view at a time, but take the last one)
    const entries = Array.from(viewportSerializers.current.entries());
    if (entries.length > 0) {
      const [viewName, serializer] = entries[entries.length - 1];
      try {
        const data = serializer();
        if (data && Object.keys(data).length > 0) {
          // Estimate tokens: ~4 chars per token
          const jsonStr = JSON.stringify(data);
          const estimatedTokens = Math.ceil(jsonStr.length / 4);

          payload.viewport = {
            view: viewName,
            capturedAt: new Date().toISOString(),
            estimatedTokens,
            data,
          };
        }
      } catch (err) {
        console.warn('⚠️ AgentContext: Viewport serializer failed for', viewName, err);
      }
    }

    return payload;
  }, [pageContext]);

  // ── Auto-update page context based on route ─────────────────────────
  //
  // The route owns the shape of the context, so a pathname change replaces it.
  // Two kinds of page-supplied enrichment survive the swap: the user's role
  // (user-scoped) and the program's name/status while the program is unchanged
  // (ProgramLayout only re-supplies those when the program itself changes).
  // Everything else — e.g. ModulePage's stepId/substage/stepName — is
  // re-applied by the page that owns it (see ModulePage).

  React.useEffect(() => {
    const newContext = deriveRouteContext(location.pathname);
    if (newContext.programId != null) {
      console.log('🎯 AgentContext: Set programId from URL:', newContext.programId, 'from path:', location.pathname);
    }
    setPageContext((prev) => {
      const carried: PageContext = {};
      if (prev.userRole) carried.userRole = prev.userRole;
      if (newContext.programId != null && prev.programId === newContext.programId) {
        if (prev.programName) carried.programName = prev.programName;
        if (prev.programStatus) carried.programStatus = prev.programStatus;
      }
      return { ...carried, ...newContext };
    });
  }, [location.pathname]);

  // ── Context value ───────────────────────────────────────────────────

  const value: AgentContextType = {
    isOpen,
    openDrawer,
    openDrawerWith,
    consumePendingMessage,
    closeDrawer,
    toggleDrawer,
    pageContext,
    updatePageContext,
    conversationId,
    setConversationId,
    startNewConversation,
    registerViewport,
    unregisterViewport,
    buildContextPayload,
  };

  return <AgentContext.Provider value={value}>{children}</AgentContext.Provider>;
};

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Hook to use agent context
 */
export const useAgent = (): AgentContextType => {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgent must be used within an AgentProvider');
  }
  return context;
};
